import { useEffect, useState } from 'react';
import { Segmented, Input, Button, FilterChip, PhotoGrid, PhotoStrip } from '@indipia/ui-core';
import { portfolioApi } from '../api/portfolioApi';
import { ThumbImg } from './ThumbImg';
import type { PhotoSearchResult, SelectedPhoto } from '../types/portfolio.types';

const DATE_PRESETS = [
  { label: '오늘', value: 'today' },
  { label: '이번 주', value: 'week' },
  { label: '최근 30일', value: '30' },
  { label: '최근 90일', value: '90' },
  { label: '전체', value: 'all' },
];

function fmt(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function presetRange(preset: string): { startDate?: string; endDate?: string } {
  const today = new Date();
  const end = fmt(today);
  if (preset === 'all') return {};
  if (preset === 'today') return { startDate: end, endDate: end };
  const d = new Date(today);
  if (preset === 'week') d.setDate(d.getDate() - 7);
  else if (preset === '30') d.setDate(d.getDate() - 30);
  else if (preset === '90') d.setDate(d.getDate() - 90);
  return { startDate: fmt(d), endDate: end };
}

/** 선택된 사진 스트립 — ui-core PhotoStrip 위임(도메인 라벨만 부여). 위치는 호출부가 정한다. */
export function SelectedStrip({
  selected,
  onRemove,
}: {
  selected: SelectedPhoto[];
  onRemove: (photoId: number, url: string) => void;
}) {
  return (
    <PhotoStrip
      items={selected.map((p) => ({ id: p.photoId, url: p.url }))}
      title={selected.length > 0 ? `선택된 사진 ${selected.length}장` : undefined}
      renderThumb={(item) => <ThumbImg url={item.url} />}
      onRemove={(id) => {
        const p = selected.find((s) => s.photoId === id);
        if (p) onRemove(p.photoId, p.url);
      }}
    />
  );
}

interface Props {
  selected: SelectedPhoto[];
  onToggle: (
    photoId: number,
    url: string,
    meta?: { spaceTag?: string | null; siteId?: number | null; siteName?: string | null },
  ) => void;
  maxHeight?: number;
  /** 그리드 상단에 선택 스트립을 함께 그릴지. false면 호출부가 별도 배치(예: 하단). */
  showStrip?: boolean;
  /** 선택된 현장으로 검색을 한정한다(현장 기반 진입). 미지정이면 전 현장. */
  siteId?: number | null;
  /** 현장명·태그 keyword 검색박스 노출(다른 현장 피커용). 기본 노출. */
  showKeyword?: boolean;
  /** 태그 칩(다중) 노출 — 결과 사진들의 공간 태그로 클라이언트단 OR 필터(선택 현장 피커용). */
  showTags?: boolean;
  /** 현장 선택 전에는 검색하지 않고 빈 그리드로 둔다(선택 현장 피커용). 미지정이면 전 현장 검색. */
  requireSite?: boolean;
  /** 자동검색 없이 검색 버튼/Enter 로만 조회한다(다른 현장 피커용). */
  manual?: boolean;
}

/** 사진 검색·다중선택 그리드 (모달·인페이지 공용) — sv-photo 위임. 표현은 ui-core PhotoGrid. */
export function PhotoSelectGrid({
  selected,
  onToggle,
  maxHeight = 420,
  showStrip = true,
  siteId,
  showKeyword = true,
  showTags = false,
  requireSite = false,
  manual = false,
}: Props) {
  const [preset, setPreset] = useState('30'); // 기간 필터 기본값 — 최근 30일
  const [keyword, setKeyword] = useState('');
  const [result, setResult] = useState<PhotoSearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [runToken, setRunToken] = useState(0); // 0 = 아직 미검색(manual 초기)

  // 현장이 바뀌면 태그 선택 초기화(새 현장의 태그 집합으로 다시)
  useEffect(() => {
    setSelectedTags(new Set());
  }, [siteId]);

  // 자동검색 트리거 — manual 모드는 자동 검색하지 않는다(검색 버튼/Enter 로만).
  useEffect(() => {
    if (manual) return;
    setRunToken((n) => n + 1);
  }, [preset, keyword, siteId, requireSite, manual]);

  function doSearch() {
    setRunToken((n) => n + 1);
  }

  // 실제 검색 — runToken 이 바뀔 때만. 미검색(manual 초기)·현장 미선택이면 빈 상태.
  useEffect(() => {
    const controller = new AbortController();
    if (runToken === 0 || (requireSite && siteId == null)) {
      setResult({ totalCount: 0, totalSiteCount: 0, sites: [] });
      setLoading(false);
      return () => controller.abort();
    }
    setLoading(true);
    const { startDate, endDate } = presetRange(preset);
    portfolioApi
      .searchPhotos(
        { keyword: keyword || undefined, startDate, endDate, siteId: siteId ?? undefined, limit: 200 },
        controller.signal,
      )
      .then((res) => setResult(res.data.data))
      .catch((err) => {
        if (controller.signal.aborted || err?.code === 'ERR_CANCELED') return;
        setResult({ totalCount: 0, totalSiteCount: 0, sites: [] });
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
    // preset/keyword/siteId 는 runToken 변경 시점의 최신값을 사용(자동 재검색은 위 트리거가 담당)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runToken]);

  // 사진에 출처 현장(siteId·siteName)을 함께 실어 배지·해시태그에 사용
  const allPhotos = (result?.sites ?? []).flatMap((s) =>
    s.photos.map((p) => ({ ...p, siteId: s.siteId, siteName: s.siteName })),
  );

  // 태그 칩 옵션 — 결과 사진들의 공간 태그 + 개수(다중 OR 필터)
  const tagCounts = new Map<string, number>();
  if (showTags) {
    allPhotos.forEach((p) => {
      const t = (p.spaceTag ?? '').trim();
      if (t) tagCounts.set(t, (tagCounts.get(t) ?? 0) + 1);
    });
  }
  const shownPhotos =
    selectedTags.size === 0
      ? allPhotos
      : allPhotos.filter((p) => p.spaceTag != null && selectedTags.has(p.spaceTag));

  function toggleTag(t: string) {
    setSelectedTags((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {showStrip && <SelectedStrip selected={selected} onRemove={onToggle} />}

      {showKeyword && (
        <div style={{ display: 'flex', gap: 8 }}>
          <Input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                doSearch();
              }
            }}
            placeholder="현장명·태그로 검색"
            width="fill"
          />
          {manual && (
            <Button variant="secondary" onClick={doSearch}>
              검색
            </Button>
          )}
        </div>
      )}
      <Segmented options={DATE_PRESETS} value={preset} onChange={setPreset} />

      {showTags && tagCounts.size > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {[...tagCounts.entries()].map(([t, c]) => (
            <FilterChip key={t} active={selectedTags.has(t)} count={c} size="sm" onClick={() => toggleTag(t)}>
              {t}
            </FilterChip>
          ))}
        </div>
      )}

      {/* 사진이 없어도 영역(박스)은 유지 — 빈 상태를 고정 높이 박스로 렌더 */}
      <div
        style={{
          minHeight: 200,
          maxHeight,
          overflowY: 'auto',
          border: '1px solid var(--shell-border)',
          borderRadius: 10,
          background: 'var(--shell-surface)',
          padding: 8,
        }}
      >
        {loading || shownPhotos.length === 0 ? (
          <div
            style={{
              minHeight: 184,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              color: 'var(--shell-on-surface-2)',
              fontSize: 13,
            }}
          >
            {loading
              ? '불러오는 중…'
              : requireSite && siteId == null
                ? '현장을 선택하면 사진이 표시됩니다.'
                : manual && runToken === 0
                  ? '현장명·태그로 검색하세요.'
                  : '해당 조건의 사진이 없습니다.'}
          </div>
        ) : (
          <PhotoGrid
            items={shownPhotos.map((p) => ({ id: p.photoId, url: p.thumbUrl }))}
            selectedIds={selected.map((p) => p.photoId)}
            renderThumb={(item) => <ThumbImg url={item.url} />}
            onToggle={(id) => {
              const photo = allPhotos.find((p) => p.photoId === id);
              if (photo)
                onToggle(photo.photoId, photo.thumbUrl, {
                  spaceTag: photo.spaceTag,
                  siteId: photo.siteId,
                  siteName: photo.siteName,
                });
            }}
          />
        )}
      </div>
    </div>
  );
}
